/* eslint-disable complexity */

import LikeIcon from "@/assets/img/symbolic/thumbs-up.svg?react";
import CommentIcon from "@/assets/img/symbolic/comments.svg?react";
import ShareIcon from "@/assets/img/symbolic/paper-plane.svg?react";
import Crown from "@/assets/img/crown.svg?react";
import type { DailyHeadlineSubmissionPost } from "@/local-shared/returnTypes";
import social from "@/api/social";
import { useCallback, useContext, useEffect, useState } from "react";
import { AuthContext } from "@/Realize";
import UIListener from "@/pages/home/UIListener";
import type { TabName } from "@/pages/home/TabData";
import { Share } from "@capacitor/share";
import { SocialButtonType } from "./DailyHeadlineSubmissionTypes";
import { HeadlineDisplay } from "./HeadlineDisplay";
import type { DailyHeadline } from "@/local-shared/model";
import IconEraser from "@/assets/img/symbolic/eraser.svg?react";

export function SocialButton({
    type,
    number,
    active,
    onClick,
}: {
    type: SocialButtonType
    number?: number;
    active?: boolean;
    onClick?: React.MouseEventHandler<HTMLButtonElement>;
})
{
    const getIcon = (type: SocialButtonType) =>
    {
        switch (type)
        {
            case SocialButtonType.Like:
                if (active)
                {
                    return (
                        <LikeIcon className="w-5 h-5 text-sharp-red" />
                    );
                }

                return (
                    <LikeIcon className="w-5 h-5" />
                );
            case SocialButtonType.Comment:
                return (
                    <CommentIcon className="w-5 h-5" />
                );
            case SocialButtonType.Share:
                return (
                    <ShareIcon className="w-5 h-5" />
                );
            default:
                return (
                    <LikeIcon className="w-5 h-5" />
                );
        }
    };


    return (
        <button
            className={Wind.style({
                display: "flex",
                flexDirection: "flex-col",
                aspectRatio: "aspect-square",
                flex: "flex-[0_0_3rem]",
                width: "w-12",
                padding: "p-1",
                alignItems: "items-center",
                justifyContent: "justify-center",
                borderRadius: "rounded-xl",
                borderWidth: "border",
                borderColor: "border-[#EFEFEF]",
                borderStyle: "border-solid",
                color: "text-[#949494]"
            }).class}
            onClick={onClick}
        >
            {getIcon(type)}
            <span
                className={Wind.style({
                    fontSize: "text-sm",
                    fontFamily: "font-inter",
                }).class}
            >
                {number}
            </span>
        </button>
    );
}

export default function DailyHeadlineSubmission({
    post,
    pastHeadline = "",
    isWinner = false,
    whiteBanner,
    bannerText = "Yesterday's Winner",
    canOpenFullscreen,
    noPadding = false,
    headline,
    onWinnerSelected,
    updatePost,
    onShowComments,
    setShareValue,
    resetSubmissions,
    onDeleteSubmission,
}: {
    post: DailyHeadlineSubmissionPost;
    canOpenFullscreen?: boolean;
    pastHeadline?: string;
    isWinner?: boolean;
    whiteBanner?: boolean;
    bannerText?: string;
    noPadding?: boolean;
    headline?: DailyHeadline;
    onWinnerSelected?: (winnerID: number) => void;
    updatePost: (post: DailyHeadlineSubmissionPost) => void;
    onShowComments?: () => void
    setShareValue?: (value: DailyHeadlineSubmissionPost | null) => void;
    resetSubmissions?: () => void;
    onDeleteSubmission?: (submissionID: number) => void;
})
{
    const userData = useContext(AuthContext);
    const [userCanShare, setUserCanShare] = useState(false);

    const [stats, setStats] = useState({
        likes: post.stats.likes,
        comments: post.stats.comments,
        hasLiked: post.hasLiked,
    });

    useEffect(() =>
    {
        const likeTrigger = (e: unknown) =>
        {
            const data = e as { postID: number; totalLikes: number; hasLiked: boolean };

            if (data.postID !== post.id)
            {
                return;
            }

            setStats((prev) =>
            {
                return {
                    ...prev,
                    likes: data.totalLikes,
                    hasLiked: data.hasLiked,
                };
            });

            updatePost({
                ...post,
                stats: {
                    ...post.stats,
                    likes: data.totalLikes,
                },
                hasLiked: data.hasLiked,
            });
        };

        UIListener.listen("SubmissionLiked", likeTrigger);

        return () =>
        {
            UIListener.unlisten("SubmissionLiked", likeTrigger);
        };
    });

    useEffect(() =>
    {
        Share.canShare().then((result) =>
        {
            setUserCanShare(result.value);
        })
            .catch((err) =>
            {
                Logger.error("Error checking if user can share:", err);
                setUserCanShare(false);
            });
    }, []);

    const ownerControls = useCallback(() =>
    {
        if (!headline)
        {
            return null;
        }

        if ( !userData)
        {
            Logger.log("No user data available, cannot show owner controls");

            return null;
        }

        const {created_by, winning_post_id} = headline;

        if (created_by !== null && created_by.toString() !== userData.id.toString() && winning_post_id === null)
        {
            return null;
        }


        if (
            typeof onWinnerSelected === "function"
            && created_by !== null
            && created_by.toString() === userData.id.toString()
            && headline.winning_post_id === null
        )
        {
            return (
                <button
                    className={Wind.style({
                        fontSize: "text-xs",
                        fontFamily: "font-inter",
                        fontWeight: "font-bold",
                        color: "text-white",
                        backgroundColor: "bg-sharp-red",
                        borderRadius: "rounded-lg",
                        display: "inline-flex",
                        alignItems: "items-center",
                        justifyContent: "justify-center",
                        paddingY: "py-2",
                        paddingX: "px-4",

                    }).class}
                    onClick={() =>
                    {
                        onWinnerSelected(post.id);
                    }}
                >
                    Select Winner
                </button>
            );
        }

        return null;
    }, [headline, userData, post.id, onWinnerSelected]);

    return (
        <div
            className={Wind.style({
                display: "flex",
                flexDirection: "flex-col",
                gap: "gap-2",
                flex: "flex-[0_0_100%]",
                height: "h-full",
                paddingY: noPadding ? "py-0" : "py-16",
                scrollSnapAlign: "snap-center",
            }).class}
        >
            {isWinner && (
                <HeadlineDisplay
                    bannerText={bannerText}
                    headline={
                        pastHeadline
                    }
                    className={Wind.style({
                        marginTop: "-mt-4",
                        marginBottom: "mb-4",
                        marginX: "-mx-4",
                        zIndex: "z-[1]",
                    }).class}
                    inverted
                    whiteBanner={whiteBanner}
                />
            )}
            <div
                className={Wind.style({
                    display: "block",
                }).class}
            >
                <div
                    className={Wind.style({
                        borderRadius: isWinner ? "rounded-none" : "rounded-xl",
                        backgroundColor: "bg-white",
                        padding: isWinner ? "p-2" : "p-5",
                        marginX: isWinner ? "mx-auto" : "mx-4",
                        marginBottom: isWinner ? "mb-[unset]" : "mb-3",
                        maxWidth: isWinner ? "max-w-[16.313rem]" : "max-w-none",
                        aspectRatio: isWinner ? "aspect-[261/369]" : "aspect-[263/338]",
                        position: isWinner ? "relative" : "static",
                    }).class}
                    onClick={() =>
                    {
                        if (canOpenFullscreen)
                        {
                            Logger.log("Clicked on post", post.id);
                            UIListener.trigger("view_daily_submission", post);
                        }
                    }}
                >
                    <img
                        src={post.photo_path}
                        className={Wind.style({
                            width: "w-full",
                            objectFit: "object-cover",
                            objectPosition: "object-center",
                            aspectRatio: isWinner ? "aspect-[261/369]" : "aspect-[263/338]",
                            borderRadius: "rounded-lg",
                            display: "block",
                        }).class}
                    />
                    {isWinner && (
                        <Crown
                            className={Wind.style({
                                position: "absolute",
                                top: "top-0",
                                right: "right-0",
                                width: "w-24",
                                transformTranslateX: "translate-x-1/2",
                                transformTranslateY: "-translate-y-1/2",
                                zIndex: "z-[3]",
                            }).class}
                        />
                    )}
                </div>
                <div
                    className={Wind.style({
                        borderRadius: "rounded-xl",
                        backgroundColor: isWinner ? "bg-dark-blue" : "bg-white",
                        color: isWinner ? "text-white" : "text-black",
                        padding: isWinner ? "p-2": "p-6",
                    }).class}
                >
                    <div
                        className={Wind.style({
                            display: "flex",
                            flexDirection: "flex-col",
                        }).class}
                    >
                        <div
                            className={Wind.style({
                                display: "flex",
                                flexDirection: "flex-row",
                                alignItems: "items-center",
                                justifyContent: "justify-between",
                            }).class}
                        >
                            <h2
                                className={Wind.style({
                                    fontSize: "text-sm",
                                    lineHeight: "leading-[100%]",
                                    fontWeight: "font-bold",
                                    fontFamily: "font-inter",
                                    marginBottom: "mb-1",
                                    display: "flex",
                                    gap: "gap-1",
                                    alignItems: "items-center",
                                    justifyContent: "justify-between",
                                    width: "w-full",
                                }).class}
                            >
                                <button
                                    onClick={(e) =>
                                    {
                                        e.stopPropagation();
                                        localStorage.setItem("profile_id", post.user_id.toString());

                                        UIListener.trigger("setActiveTab", "profile" as TabName);

                                        resetSubmissions?.();
                                    }}
                                >
                                    {`@${post.username}`}
                                </button>
                                {userData?.id.toString() === post.user_id.toString() && (
                                    <button
                                        className={Wind.style({
                                            backgroundColor: "bg-sharp-red",
                                            color: "text-white",
                                            padding: "p-2",
                                            borderRadius: "rounded-md",
                                        }).class}

                                        onClick={(e) =>
                                        {
                                            e.stopPropagation();
                                            onDeleteSubmission?.(post.id);
                                        }}
                                    >
                                        <IconEraser
                                            className={Wind.style({
                                                color: "text-white",
                                                width: "w-4",
                                                height: "h-4",
                                            }).class}
                                        />
                                    </button>
                                )}
                            </h2>
                            {ownerControls()}
                        </div>
                        <p
                            className={Wind.style({
                                fontSize: "text-sm",
                                lineHeight: "leading-[100%]",
                                fontFamily: "font-inter",
                                color: isWinner ? "text-[#C7E0FF]" : "text-black/70",
                                marginBottom: "mb-3",
                            }).class}
                        >
                            {post.description}
                        </p>
                        <div
                            className={Wind.style({
                                display: "flex",
                                flexDirection: "flex-row",
                                gap: "gap-2",
                                alignItems: "items-center",
                                justifyContent: "justify-end",
                                fontSize: "text-sm",
                                color: isWinner ? "text-white" : "text-black/60",
                            }).class}
                        >
                            <div
                                className={Wind.style({
                                    display: "grid",
                                    gridTemplateColumns: userCanShare
                                        ? "grid-cols-[repeat(3,minmax(0,1fr))]"
                                        :"grid-cols-[repeat(3,minmax(0,1fr))]",
                                    gap: "gap-2",
                                    alignItems: "items-center",
                                    justifyContent: "justify-between",
                                }).class}
                            >
                                <SocialButton
                                    active={userData !== null && stats.hasLiked}
                                    type={
                                        SocialButtonType.Like
                                    }
                                    number={stats.likes}
                                    onClick={(e) =>
                                    {
                                        e.stopPropagation();

                                        if (userData !== null)
                                        {
                                            social.likeSubmission(post.id).then((data) =>
                                            {
                                                if (data)
                                                {
                                                    // Reload
                                                    setStats((prev) =>
                                                    {
                                                        return {
                                                            ...prev,
                                                            likes: data.totalLikes,
                                                            hasLiked: !prev.hasLiked,
                                                        };
                                                    });

                                                    updatePost({
                                                        ...post,
                                                        stats: {
                                                            ...post.stats,
                                                            likes: data.totalLikes,
                                                        },
                                                        hasLiked: !post.hasLiked,
                                                    });
                                                }
                                            })
                                                .catch((error) =>
                                                {
                                                    Logger.error("Error liking submission:", error);
                                                });
                                        }
                                        else
                                        {
                                            UIListener.trigger("ShowLoginPopUp", {});
                                        }
                                    }}
                                />
                                <SocialButton
                                    type={
                                        SocialButtonType.Comment
                                    }
                                    number={post.stats.comments}
                                    onClick={(e) =>
                                    {
                                        e.stopPropagation();

                                        if (onShowComments)
                                        {
                                            onShowComments();
                                        }
                                    }}
                                />
                                <SocialButton
                                    type={
                                        SocialButtonType.Share
                                    }
                                    onClick={() =>
                                    {
                                        setShareValue?.(post);
                                    }}
                                />

                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
